<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import ScheduledDateTimePicker from 'dashboard/components/ui/ScheduledDateTimePicker.vue';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import { getContactDisplayName } from 'dashboard/helper/contactDisplayName';

const route = useRoute();
const { t } = useI18n();
const accountId = computed(() => route.params.accountId);
const pipelines = ref([]);
const opportunities = ref([]);
const metrics = ref({});
const teams = ref([]);
const agents = ref([]);
const stageMeta = ref({});
const loadingStages = ref({});
const selectedPipelineId = ref(null);
const loading = ref(true);
const draggingId = ref(null);
const showCreate = ref(false);
const showSettings = ref(false);
const showAiContext = ref(false);
const showOpportunityDetails = ref(false);
const showConversationPreview = ref(false);
const showLostReasonModal = ref(false);
const showWonConfirmationModal = ref(false);
const showShopifyCheckoutModal = ref(false);
const showBoardFilters = ref(false);
const showPipelineHeader = ref(true);
const showCurrencyMenu = ref(false);
const showCreateCurrencyMenu = ref(false);
const confirmDialog = ref(null);
const confirmTitle = ref('');
const confirmDescription = ref('');
const displayCurrency = ref('BRL');
const lostReasonDraft = ref('');
const pendingLostMove = ref(null);
const pendingWonMove = ref(null);
const selectedOpportunity = ref(null);
const selectedShopifyOpportunity = ref(null);
const opportunityDraft = ref({});
const loadingOpportunityDetails = ref(false);
const loadingMoreEvents = ref(false);
const eventsMeta = ref({});
const autofillingOpportunity = ref(false);
const resolvingConversationId = ref(null);
const generatingSummaryConversationId = ref(null);
const loadingConversationPreview = ref(false);
const previewMessages = ref([]);
const form = ref({
  conversation: null,
  amount: '',
  currency: 'BRL',
  expected_close_at: '',
});
const pipelineDraft = ref({ name: '', stages: [] });
const conversationQuery = ref('');
const conversationCandidates = ref([]);
const searchingConversations = ref(false);
let conversationSearchTimeout;
const initialActivityRange = [
  new Date(new Date().setDate(new Date().getDate() - 6)),
  new Date(),
];
const activityDateRange = ref(initialActivityRange);
const activityRangeType = ref(DATE_RANGE_TYPES.LAST_7_DAYS);
const boardFilters = ref({
  teamId: '',
  assigneeId: '',
  quickFilter: '',
});
let loadMetrics;
let loadStage;
let loadPipelineOpportunities;
let moveOpportunity;

const selectedPipeline = computed(() =>
  pipelines.value.find(item => item.id === Number(selectedPipelineId.value))
);
const stages = computed(() => selectedPipeline.value?.stages || []);
const activeStages = computed(() => stages.value.filter(stage => stage.active));
const localDate = date =>
  [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');
const activityPeriod = computed(() => {
  return {
    from: localDate(activityDateRange.value[0]),
    to: localDate(activityDateRange.value[1]),
  };
});
const boardFilterParams = () => ({
  team_id: boardFilters.value.teamId || undefined,
  assignee_id: boardFilters.value.assigneeId || undefined,
  activity_from: activityPeriod.value.from || undefined,
  activity_to: activityPeriod.value.to || undefined,
  quick_filter: boardFilters.value.quickFilter || undefined,
  display_currency: displayCurrency.value,
});
const selectedOpportunityIsLost = computed(
  () => selectedOpportunity.value?.sales_pipeline_stage?.kind === 'lost'
);
const primaryCurrency = computed(() => {
  const oppWithCurrency = opportunities.value.find(item => item.currency);
  return oppWithCurrency?.currency || 'BRL';
});
const formatCurrency = (value, currencyCode = null) => {
  const code = (
    currencyCode ||
    displayCurrency.value ||
    primaryCurrency.value ||
    'BRL'
  ).toUpperCase();
  const locale = code === 'USD' ? 'en-US' : 'pt-BR';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: code,
  }).format(Number(value || 0));
};
const currency = formatCurrency;
const contactDisplayName = contact =>
  getContactDisplayName(contact) || 'Contato sem nome';
const currencyLabel = code =>
  ({
    BRL: 'R$ (BRL)',
    USD: 'US$ (USD)',
    EUR: '€ (EUR)',
  })[code] || 'R$ (BRL)';
const selectOpportunityCurrency = code => {
  opportunityDraft.value.currency = code;
  showCurrencyMenu.value = false;
};
const selectCreateCurrency = code => {
  form.value.currency = code;
  showCreateCurrencyMenu.value = false;
};
const opportunitiesFor = stageId =>
  opportunities.value.filter(item => item.sales_pipeline_stage_id === stageId);
const formatDateTime = value =>
  value
    ? new Intl.DateTimeFormat('pt-BR', {
        dateStyle: 'short',
        timeStyle: 'short',
      }).format(new Date(value))
    : 'Sem mensagens registradas';

const isIncomingMessage = message =>
  message.message_type === 0 || message.message_type === 'incoming';
const healthLabel = health =>
  ({
    healthy: 'Em dia',
    attention: 'Atenção',
    overdue: 'Atrasada',
    closed: 'Encerrada',
  })[health] || 'Atenção';
const healthClass = health =>
  ({
    healthy: 'bg-n-teal/15 text-n-teal-11',
    attention: 'bg-n-amber/15 text-n-amber-11',
    overdue: 'bg-n-ruby/15 text-n-ruby-11',
    closed: 'bg-n-slate-3 text-n-slate-11',
  })[health] || 'bg-n-slate-3 text-n-slate-11';
const shopifyCheckoutLabel = status =>
  ({
    checkout_created: 'Checkout enviado',
    order_created: 'Pedido criado',
    paid: 'Pagamento confirmado',
    cancelled: 'Pedido cancelado',
    abandoned: 'Checkout abandonado',
    active: 'Seleção em andamento',
  })[status] || status;
const shopifyCheckoutClass = status =>
  ({
    paid: 'text-n-teal-11',
    abandoned: 'text-n-amber-11',
    cancelled: 'text-n-ruby-11',
  })[status] || 'text-n-slate-12';
const openShopifyCheckout = opportunity => {
  selectedShopifyOpportunity.value = opportunity;
  showShopifyCheckoutModal.value = true;
};

const canGenerateSummary = conversation => {
  if (!conversation?.last_public_message_at) return false;
  if (!conversation.latest_lumerai_summary_at) return true;

  return (
    new Date(conversation.last_public_message_at) >
    new Date(conversation.latest_lumerai_summary_at)
  );
};

const canAutofillWithAi = opportunity => {
  const latestMessageId = Number(
    opportunity?.conversation?.last_public_message_id || 0
  );
  const analyzedMessageId = Number(
    opportunity?.lumerai_autofill_message_id || 0
  );
  return latestMessageId > 0 && latestMessageId > analyzedMessageId;
};

const openConversation = async opportunity => {
  const conversation = opportunity.conversation;
  if (!conversation?.display_id) return;

  selectedOpportunity.value = opportunity;
  previewMessages.value = [];
  showConversationPreview.value = true;
  loadingConversationPreview.value = true;

  try {
    const response = await window.axios.get(
      `/api/v1/accounts/${accountId.value}/conversations/${conversation.display_id}/messages`
    );
    previewMessages.value = response.data.payload || [];
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        'Não foi possível carregar as mensagens deste atendimento.'
    );
  } finally {
    loadingConversationPreview.value = false;
  }
};

const openFullConversation = opportunity => {
  const conversation = opportunity.conversation;
  if (!conversation?.display_id) return;
  window.location.assign(
    `/app/accounts/${accountId.value}/conversations/${conversation.display_id}`
  );
};

const openAiContext = opportunity => {
  selectedOpportunity.value = opportunity;
  showAiContext.value = true;
};

const loadOpportunityDetails = async (
  opportunity,
  { page = 1, append = false } = {}
) => {
  const response = await window.axios.get(
    `/api/v1/accounts/${accountId.value}/sales_opportunities/${opportunity.id}`,
    { params: { events_page: page, events_per_page: 10 } }
  );
  const payload = response.data.payload;

  selectedOpportunity.value = append
    ? {
        ...payload,
        events: [
          ...(selectedOpportunity.value?.events || []),
          ...(payload.events || []),
        ],
      }
    : payload;
  eventsMeta.value = response.data.meta?.events || {};
};

const openOpportunityDetails = async opportunity => {
  selectedOpportunity.value = opportunity;
  showCurrencyMenu.value = false;
  eventsMeta.value = {};
  opportunityDraft.value = {
    amount: opportunity.amount || '',
    currency: opportunity.currency || primaryCurrency.value || 'BRL',
    expected_close_at: opportunity.expected_close_at?.slice(0, 10) || '',
    next_action: opportunity.next_action || '',
    next_action_at: opportunity.next_action_at?.slice(0, 16) || '',
    priority: opportunity.priority || 'normal',
    probability: opportunity.probability ?? 0,
    forecast_category: opportunity.forecast_category || 'pipeline',
  };
  showOpportunityDetails.value = true;
  loadingOpportunityDetails.value = true;
  try {
    await loadOpportunityDetails(opportunity);
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        'Não foi possível carregar o histórico comercial.'
    );
  } finally {
    loadingOpportunityDetails.value = false;
  }
};

const openOpportunityFromRoute = async () => {
  const opportunityId = Number(route.query.opportunity_id);
  if (!opportunityId || selectedOpportunity.value?.id === opportunityId) return;

  try {
    const response = await window.axios.get(
      `/api/v1/accounts/${accountId.value}/sales_opportunities/${opportunityId}`
    );
    const opportunity = response.data.payload;

    if (selectedPipelineId.value !== opportunity.sales_pipeline_id) {
      selectedPipelineId.value = opportunity.sales_pipeline_id;
      await Promise.all([loadPipelineOpportunities(), loadMetrics()]);
    }

    await openOpportunityDetails(opportunity);
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        'Não foi possível abrir a oportunidade vinculada.'
    );
  }
};

const loadMoreEvents = async () => {
  const nextPage = eventsMeta.value.next_page;
  const opportunity = selectedOpportunity.value;
  if (!nextPage || !opportunity || loadingMoreEvents.value) return;

  loadingMoreEvents.value = true;
  try {
    await loadOpportunityDetails(opportunity, { page: nextPage, append: true });
  } catch (error) {
    useAlert(
      error.response?.data?.error || 'Não foi possível carregar mais eventos.'
    );
  } finally {
    loadingMoreEvents.value = false;
  }
};

const eventLabel = event => {
  const labels = {
    stage_changed: 'Etapa alterada',
    next_action_updated: 'Próximo passo atualizado',
    created: 'Oportunidade criada',
  };

  return labels[event.event_type] || 'Dados atualizados';
};

const stageTransition = event => {
  if (event.event_type !== 'stage_changed') return null;

  const fromStage = event.from_stage_name || event.metadata?.from_stage_name;
  const toStage = event.to_stage_name || event.metadata?.to_stage_name;
  return fromStage && toStage ? `${fromStage} → ${toStage}` : null;
};

const saveCommercialDetails = async () => {
  const opportunity = selectedOpportunity.value;
  if (!opportunity || selectedOpportunityIsLost.value) return;

  try {
    const response = await window.axios.patch(
      `/api/v1/accounts/${accountId.value}/sales_opportunities/${opportunity.id}`,
      { sales_opportunity: opportunityDraft.value }
    );
    await loadStage(opportunity.sales_pipeline_stage_id, { reset: true });
    selectedOpportunity.value = response.data.payload;
    useAlert('Dados comerciais atualizados.');
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        'Não foi possível atualizar os dados comerciais.'
    );
  }
};

const fillCommercialDetailsWithAi = async () => {
  const opportunity = selectedOpportunity.value;
  if (
    !opportunity?.conversation ||
    selectedOpportunityIsLost.value ||
    autofillingOpportunity.value
  )
    return;

  autofillingOpportunity.value = true;
  try {
    const response = await window.axios.post(
      `/api/v1/accounts/${accountId.value}/sales_opportunities/${opportunity.id}/autofill`
    );
    if (response.data.up_to_date) {
      useAlert(response.data.message);
      return;
    }

    const suggestion = response.data.payload || {};

    Object.entries(suggestion).forEach(([field, value]) => {
      if (value !== '' && value !== null && value !== undefined)
        opportunityDraft.value[field] = value;
    });
    selectedOpportunity.value = {
      ...selectedOpportunity.value,
      lumerai_autofill_message_id: response.data.analyzed_message_id,
    };
    useAlert(
      'Campos encontrados foram preenchidos. Revise os dados antes de salvar.'
    );
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        'Não foi possível preencher os dados com a LumerAI.'
    );
  } finally {
    autofillingOpportunity.value = false;
  }
};

const resolveConversation = async opportunity => {
  const conversation = opportunity.conversation;
  if (!conversation?.display_id || conversation.status === 'resolved') return;

  resolvingConversationId.value = conversation.id;
  try {
    await window.axios.post(
      `/api/v1/accounts/${accountId.value}/conversations/${conversation.display_id}/toggle_status`,
      {
        status: 'resolved',
      }
    );
    opportunity.conversation.status = 'resolved';
    useAlert(
      'Atendimento resolvido. A oportunidade continua no Pipeline até ser marcada como ganha ou perdida.'
    );
  } catch (error) {
    useAlert(
      error.response?.data?.error || 'Não foi possível resolver o atendimento.'
    );
  } finally {
    resolvingConversationId.value = null;
  }
};

const generateSummary = async () => {
  const conversation = selectedOpportunity.value?.conversation;
  if (!conversation?.display_id || generatingSummaryConversationId.value)
    return;

  generatingSummaryConversationId.value = conversation.id;
  try {
    const response = await window.axios.post(
      `/api/v1/accounts/${accountId.value}/lumerai/tasks/summarize`,
      {
        conversation_display_id: conversation.display_id,
      }
    );
    if (response.data.up_to_date) {
      useAlert(response.data.message);
      return;
    }
    await loadStage(selectedOpportunity.value.sales_pipeline_stage_id, {
      reset: true,
    });
    selectedOpportunity.value =
      opportunities.value.find(
        item => item.id === selectedOpportunity.value?.id
      ) || selectedOpportunity.value;
    useAlert('Resumo atualizado e adicionado ao histórico da LumerAI.');
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        'Não foi possível gerar o resumo da LumerAI.'
    );
  } finally {
    generatingSummaryConversationId.value = null;
  }
};

const load = async () => {
  loading.value = true;
  try {
    const [pipelinesResponse, teamsResponse, agentsResponse] =
      await Promise.all([
        window.axios.get(`/api/v1/accounts/${accountId.value}/sales_pipelines`),
        window.axios.get(`/api/v1/accounts/${accountId.value}/teams`),
        window.axios.get(`/api/v1/accounts/${accountId.value}/agents`),
      ]);
    pipelines.value = pipelinesResponse.data.payload;
    teams.value = teamsResponse.data.payload || teamsResponse.data || [];
    agents.value = agentsResponse.data.payload || agentsResponse.data || [];
    selectedPipelineId.value ||=
      pipelines.value.find(item => item.is_default)?.id ||
      pipelines.value[0]?.id;
    if (selectedPipelineId.value)
      await Promise.all([loadPipelineOpportunities(), loadMetrics()]);
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        'Não foi possível carregar o Pipeline de Vendas.'
    );
  } finally {
    loading.value = false;
  }
};

const onPipelineChange = async () => {
  selectedPipelineId.value = Number(selectedPipelineId.value);
  await Promise.all([loadPipelineOpportunities(), loadMetrics()]);
};

const applyBoardFilters = async () => {
  await Promise.all([loadPipelineOpportunities(), loadMetrics()]);
};

const onActivityDateRangeChange = async ([startDate, endDate, rangeType]) => {
  activityDateRange.value = [startDate, endDate];
  activityRangeType.value = rangeType;
  await Promise.all([loadPipelineOpportunities(), loadMetrics()]);
};

const clearBoardFilters = async () => {
  boardFilters.value = {
    teamId: '',
    assigneeId: '',
    quickFilter: '',
  };
  await Promise.all([loadPipelineOpportunities(), loadMetrics()]);
};

loadMetrics = async () => {
  const response = await window.axios.get(
    `/api/v1/accounts/${accountId.value}/sales_opportunities/metrics`,
    {
      params: {
        sales_pipeline_id: selectedPipelineId.value,
        ...boardFilterParams(),
      },
    }
  );
  metrics.value = response.data.payload || {};
};

loadStage = async (stageId, { reset = false } = {}) => {
  const previousMeta = stageMeta.value[stageId];
  const page = reset ? 1 : previousMeta?.next_page || 1;
  if (!reset && previousMeta && !previousMeta.next_page) return;

  loadingStages.value = { ...loadingStages.value, [stageId]: true };
  try {
    const response = await window.axios.get(
      `/api/v1/accounts/${accountId.value}/sales_opportunities`,
      {
        params: {
          sales_pipeline_id: selectedPipelineId.value,
          sales_pipeline_stage_id: stageId,
          page,
          per_page: 15,
          ...boardFilterParams(),
        },
      }
    );
    const loaded = response.data.payload || [];
    const existingOpportunities = reset
      ? opportunities.value.filter(
          item => item.sales_pipeline_stage_id !== stageId
        )
      : opportunities.value;
    opportunities.value = [
      ...existingOpportunities,
      ...loaded.filter(
        item => !existingOpportunities.some(existing => existing.id === item.id)
      ),
    ];
    stageMeta.value = {
      ...stageMeta.value,
      [stageId]: response.data.meta || {},
    };
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        'Não foi possível carregar as oportunidades desta etapa.'
    );
  } finally {
    loadingStages.value = { ...loadingStages.value, [stageId]: false };
  }
};

loadPipelineOpportunities = async () => {
  opportunities.value = [];
  stageMeta.value = {};
  await Promise.all(
    activeStages.value.map(stage => loadStage(stage.id, { reset: true }))
  );
};

const move = async stage => {
  if (!draggingId.value) return;
  const opportunity = opportunities.value.find(
    item => item.id === draggingId.value
  );
  const stageId = stage.id;
  if (!opportunity || opportunity.sales_pipeline_stage_id === stageId) return;
  if (opportunity.sales_pipeline_stage?.kind === 'lost') {
    useAlert(
      'Uma oportunidade perdida é final e não pode voltar para outra etapa.'
    );
    draggingId.value = null;
    return;
  }
  if (stage.kind === 'lost') {
    pendingLostMove.value = { opportunity, stage };
    lostReasonDraft.value = '';
    showLostReasonModal.value = true;
    return;
  }
  if (stage.kind === 'won') {
    const missingFields = [];
    if (!(Number(opportunity.amount) > 0)) missingFields.push('valor estimado');
    if (!opportunity.expected_close_at)
      missingFields.push('previsão de fechamento');
    if (!opportunity.next_action?.trim()) missingFields.push('próximo passo');
    if (missingFields.length) {
      useAlert(
        `Preencha ${missingFields.join(', ')} antes de marcar como ganho.`
      );
      draggingId.value = null;
      return;
    }
    pendingWonMove.value = { opportunity, stage };
    showWonConfirmationModal.value = true;
    return;
  }
  await moveOpportunity(opportunity, stage, null);
};

const cancelWonMove = () => {
  showWonConfirmationModal.value = false;
  pendingWonMove.value = null;
  draggingId.value = null;
};

const confirmWonMove = async () => {
  const pending = pendingWonMove.value;
  if (!pending) return;

  showWonConfirmationModal.value = false;
  pendingWonMove.value = null;
  await moveOpportunity(pending.opportunity, pending.stage, null);
};

const cancelLostMove = () => {
  showLostReasonModal.value = false;
  pendingLostMove.value = null;
  lostReasonDraft.value = '';
  draggingId.value = null;
};

const confirmLostMove = async () => {
  const pending = pendingLostMove.value;
  const reason = lostReasonDraft.value.trim();
  if (!pending || !reason) {
    useAlert('Informe o motivo da perda para continuar.');
    return;
  }

  showLostReasonModal.value = false;
  pendingLostMove.value = null;
  lostReasonDraft.value = '';
  await moveOpportunity(pending.opportunity, pending.stage, reason);
};

moveOpportunity = async (opportunity, stage, lostReason) => {
  const stageId = stage.id;
  const previousStageId = opportunity.sales_pipeline_stage_id;
  try {
    const response = await window.axios.patch(
      `/api/v1/accounts/${accountId.value}/sales_opportunities/${opportunity.id}/move`,
      {
        sales_pipeline_stage_id: stageId,
        lost_reason: lostReason,
        lost_reason_code: stage.kind === 'lost' ? 'other' : null,
      }
    );

    const movedOpportunity = response.data.payload;
    opportunities.value = opportunities.value.map(item =>
      item.id === movedOpportunity.id ? { ...item, ...movedOpportunity } : item
    );

    await Promise.all([
      loadStage(previousStageId, { reset: true }),
      loadStage(movedOpportunity.sales_pipeline_stage_id, { reset: true }),
    ]);
    loadMetrics().catch(() => {});
  } catch (error) {
    useAlert(
      error.response?.data?.error || 'Não foi possível mover a oportunidade.'
    );
  } finally {
    draggingId.value = null;
  }
};

const createOpportunity = async () => {
  const entryStage =
    activeStages.value.find(stage => stage.name === 'Entrada') ||
    activeStages.value[0];
  if (!form.value.conversation || !entryStage) {
    useAlert('Selecione uma conversa para incluir no Pipeline.');
    return;
  }
  try {
    await window.axios.post(
      `/api/v1/accounts/${accountId.value}/sales_opportunities`,
      {
        sales_opportunity: {
          sales_pipeline_id: selectedPipelineId.value,
          sales_pipeline_stage_id: entryStage.id,
          conversation_id: form.value.conversation.id,
          amount: form.value.amount || null,
          currency: form.value.currency || 'BRL',
          expected_close_at: form.value.expected_close_at || null,
        },
      }
    );
    await loadStage(entryStage.id, { reset: true });
    await loadMetrics();
    form.value = {
      conversation: null,
      amount: '',
      currency: 'BRL',
      expected_close_at: '',
    };
    conversationQuery.value = '';
    conversationCandidates.value = [];
    showCreateCurrencyMenu.value = false;
    showCreate.value = false;
  } catch (error) {
    useAlert(
      error.response?.data?.error || 'Não foi possível criar a oportunidade.'
    );
  }
};

const loadConversationCandidates = async () => {
  if (conversationQuery.value.trim().length < 2) {
    conversationCandidates.value = [];
    return;
  }

  searchingConversations.value = true;
  try {
    const response = await window.axios.get(
      `/api/v1/accounts/${accountId.value}/sales_opportunities/available_conversations`,
      {
        params: { q: conversationQuery.value.trim() },
      }
    );
    conversationCandidates.value = response.data.payload;
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        'Não foi possível buscar conversas disponíveis.'
    );
  } finally {
    searchingConversations.value = false;
  }
};

watch(conversationQuery, () => {
  window.clearTimeout(conversationSearchTimeout);
  conversationSearchTimeout = window.setTimeout(
    loadConversationCandidates,
    250
  );
});

watch(
  () => route.query.opportunity_id,
  () => {
    openOpportunityFromRoute().catch(() => {});
  }
);

const openPipelineSettings = () => {
  pipelineDraft.value = {
    name: selectedPipeline.value.name,
    stages: stages.value.map(stage => ({ ...stage, isNew: false })),
  };
  showSettings.value = true;
};

const addStage = () => {
  pipelineDraft.value.stages.push({
    name: 'Nova etapa',
    kind: 'open',
    color: '#64748B',
    active: true,
    default_probability: 10,
    ai_auto_move: true,
    lumerai_instruction: 'Descreva como a LumerAI deve interpretar esta etapa.',
    system_stage: false,
    position: pipelineDraft.value.stages.length + 1,
    isNew: true,
  });
};

const removeDraftStage = async (stage, index) => {
  if (stage.isNew) {
    pipelineDraft.value.stages.splice(index, 1);
    return;
  }
  if (stage.system_stage) {
    useAlert(
      'Esta é uma etapa padrão. Você pode desativá-la quando ela não tiver oportunidades.'
    );
    return;
  }
  confirmTitle.value = t('SALES_PIPELINE.DELETE_STAGE_TITLE');
  confirmDescription.value = t('SALES_PIPELINE.DELETE_STAGE_DESCRIPTION', {
    name: stage.name,
  });
  const confirmed = await confirmDialog.value?.showConfirmation();
  if (!confirmed) return;
  try {
    await window.axios.delete(
      `/api/v1/accounts/${accountId.value}/sales_pipelines/${selectedPipelineId.value}/stages/${stage.id}`
    );
    pipelineDraft.value.stages.splice(index, 1);
  } catch (error) {
    useAlert(
      error.response?.data?.error || 'Não foi possível excluir esta etapa.'
    );
  }
};

const savePipelineSettings = async () => {
  try {
    await window.axios.patch(
      `/api/v1/accounts/${accountId.value}/sales_pipelines/${selectedPipelineId.value}`,
      {
        sales_pipeline: { name: pipelineDraft.value.name },
      }
    );
    await Promise.all(
      pipelineDraft.value.stages.map((stage, index) => {
        const payload = {
          name: stage.name,
          kind: stage.kind,
          color: stage.color,
          active: stage.active,
          position: index + 1,
          lumerai_instruction: stage.lumerai_instruction || '',
          default_probability: stage.default_probability || 0,
          ai_auto_move: stage.ai_auto_move,
        };
        if (stage.isNew) {
          return window.axios.post(
            `/api/v1/accounts/${accountId.value}/sales_pipelines/${selectedPipelineId.value}/stages`,
            {
              sales_pipeline_stage: payload,
            }
          );
        }
        return window.axios.patch(
          `/api/v1/accounts/${accountId.value}/sales_pipelines/${selectedPipelineId.value}/stages/${stage.id}`,
          {
            sales_pipeline_stage: payload,
          }
        );
      })
    );
    showSettings.value = false;
    await load();
  } catch (error) {
    useAlert(error.response?.data?.error || 'Não foi possível salvar o funil.');
  }
};

onMounted(async () => {
  await load();
  await openOpportunityFromRoute();
});
</script>

<template>
  <main
    class="flex h-full min-h-0 min-w-0 flex-col overflow-hidden bg-n-background"
  >
    <button
      v-if="!showPipelineHeader"
      type="button"
      class="shrink-0 flex h-8 w-full items-center justify-center border-b border-n-weak text-n-slate-11 hover:bg-n-solid-1"
      :title="t('SALES_PIPELINE.SHOW_HEADER')"
      :aria-label="t('SALES_PIPELINE.SHOW_HEADER')"
      @click="showPipelineHeader = true"
    >
      <i class="i-lucide-chevron-down size-5" />
    </button>
    <header
      v-if="showPipelineHeader"
      class="shrink-0 px-5 py-5 border-b border-n-weak flex flex-col gap-4 md:flex-row md:items-center md:justify-between"
    >
      <div>
        <h1 class="text-xl font-semibold text-n-slate-12">
          {{ t('SALES_PIPELINE.TITLE') }}
        </h1>
        <p class="text-sm text-n-slate-11 mt-1">
          {{ t('SALES_PIPELINE.DESCRIPTION') }}
        </p>
      </div>
      <div
        class="grid w-full min-w-0 grid-cols-[minmax(0,1fr)_auto_auto_auto_auto_auto] gap-2 md:w-auto md:min-w-[47rem]"
      >
        <div class="relative min-w-0">
          <select
            v-model="selectedPipelineId"
            class="h-full w-full min-w-0 appearance-none rounded-lg border border-n-weak bg-n-solid-2 py-2 pl-3 pr-10 text-sm font-medium text-n-slate-12"
            @change="onPipelineChange"
          >
            <option
              v-for="pipeline in pipelines"
              :key="pipeline.id"
              :value="pipeline.id"
            >
              {{ pipeline.name }}
            </option>
          </select>
          <span
            class="pointer-events-none absolute inset-y-0 right-0 flex w-10 items-center justify-center"
            ><i class="i-lucide-chevron-down size-4 text-n-slate-11"
          /></span>
        </div>
        <div class="relative min-w-[7.5rem]">
          <select
            v-model="displayCurrency"
            class="h-full w-full appearance-none rounded-lg border border-n-weak bg-n-solid-2 py-2 pl-3 pr-9 text-sm font-medium text-n-slate-12"
            :title="t('SALES_PIPELINE.CONVERT_VALUES')"
            :aria-label="t('SALES_PIPELINE.DISPLAY_VALUES')"
            @change="applyBoardFilters"
          >
            <option value="BRL">{{ t('SALES_PIPELINE.DISPLAY_BRL') }}</option>
            <option value="USD">{{ t('SALES_PIPELINE.DISPLAY_USD') }}</option>
            <option value="EUR">{{ t('SALES_PIPELINE.DISPLAY_EUR') }}</option>
          </select>
          <span
            class="pointer-events-none absolute inset-y-0 right-0 flex w-9 items-center justify-center"
            ><i class="i-lucide-chevron-down size-4 text-n-slate-11"
          /></span>
        </div>
        <button
          class="whitespace-nowrap rounded-lg border border-n-weak px-4 py-2 text-sm font-medium text-n-slate-12"
          @click="openPipelineSettings"
        >
          {{ t('SALES_PIPELINE.CONFIGURE') }}
        </button>
        <button
          class="whitespace-nowrap rounded-lg border border-n-weak px-4 py-2 text-sm font-medium text-n-slate-12"
          @click="showBoardFilters = !showBoardFilters"
        >
          <i class="i-lucide-sliders-horizontal mr-1.5 inline-block size-4" />{{
            t('SALES_PIPELINE.FILTERS')
          }}
        </button>
        <button
          class="whitespace-nowrap rounded-lg bg-n-brand px-4 py-2 text-sm font-medium text-white"
          @click="showCreate = true"
        >
          {{ t('SALES_PIPELINE.LINK_CONVERSATION') }}
        </button>
        <button
          type="button"
          class="grid size-10 place-items-center rounded-lg border border-n-weak text-n-slate-11 hover:bg-n-solid-2"
          :title="t('SALES_PIPELINE.HIDE_HEADER')"
          :aria-label="t('SALES_PIPELINE.HIDE_HEADER')"
          @click="showPipelineHeader = false"
        >
          <i class="i-lucide-chevron-up size-5" />
        </button>
      </div>
    </header>

    <section
      v-if="!loading"
      class="shrink-0 grid grid-cols-2 gap-2 border-b border-n-weak bg-n-solid-1 px-5 py-3 sm:grid-cols-3 xl:grid-cols-6"
    >
      <div class="rounded-lg bg-n-solid-2 px-3 py-2">
        <p
          class="text-[11px] font-medium uppercase tracking-wide text-n-slate-10"
        >
          {{ t('SALES_PIPELINE.OPEN') }}
        </p>
        <p class="mt-1 text-base font-semibold text-n-slate-12">
          {{ metrics.open_count || 0 }}
        </p>
      </div>
      <div class="rounded-lg bg-n-solid-2 px-3 py-2">
        <p
          class="text-[11px] font-medium uppercase tracking-wide text-n-slate-10"
        >
          {{ t('SALES_PIPELINE.PIPELINE') }}
        </p>
        <p class="mt-1 text-base font-semibold text-n-slate-12">
          {{ currency(metrics.open_amount, metrics.display_currency) }}
        </p>
      </div>
      <div class="rounded-lg bg-n-solid-2 px-3 py-2">
        <p
          class="text-[11px] font-medium uppercase tracking-wide text-n-slate-10"
        >
          {{ t('SALES_PIPELINE.WEIGHTED') }}
        </p>
        <p class="mt-1 text-base font-semibold text-n-slate-12">
          {{ currency(metrics.weighted_amount, metrics.display_currency) }}
        </p>
      </div>
      <div class="rounded-lg bg-n-solid-2 px-3 py-2">
        <p
          class="text-[11px] font-medium uppercase tracking-wide text-n-slate-10"
        >
          {{ t('SALES_PIPELINE.WON') }}
        </p>
        <p class="mt-1 text-base font-semibold text-n-teal-11">
          {{ currency(metrics.won_amount, metrics.display_currency) }}
        </p>
      </div>
      <div class="rounded-lg bg-n-solid-2 px-3 py-2">
        <p
          class="text-[11px] font-medium uppercase tracking-wide text-n-slate-10"
        >
          {{ t('SALES_PIPELINE.NEXT_ACTIONS') }}
        </p>
        <p
          class="mt-1 text-base font-semibold"
          :class="
            metrics.overdue_actions_count ? 'text-n-ruby-11' : 'text-n-slate-12'
          "
        >
          {{
            t('SALES_PIPELINE.OVERDUE_ACTIONS', {
              count: metrics.overdue_actions_count || 0,
            })
          }}
        </p>
      </div>
      <div class="rounded-lg bg-n-solid-2 px-3 py-2">
        <p
          class="text-[11px] font-medium uppercase tracking-wide text-n-slate-10"
        >
          {{ t('SALES_PIPELINE.ATTENTION') }}
        </p>
        <p
          class="mt-1 text-base font-semibold"
          :class="
            metrics.attention_count ? 'text-n-amber-11' : 'text-n-slate-12'
          "
        >
          {{ metrics.attention_count || 0 }}
        </p>
      </div>
    </section>

    <section
      v-if="!loading && showBoardFilters"
      class="shrink-0 border-b border-n-weak bg-n-solid-1 px-5 py-3"
    >
      <div class="flex flex-wrap items-end gap-2">
        <div class="activity-date-picker relative shrink-0">
          <WootDatePicker
            v-model:date-range="activityDateRange"
            v-model:range-type="activityRangeType"
            position="left"
            @date-range-changed="onActivityDateRangeChange"
          />
        </div>
        <label
          class="relative flex h-14 w-52 shrink-0 flex-col gap-1 text-xs font-medium text-n-slate-11"
        >
          <span>{{ t('SALES_PIPELINE.TEAM') }}</span>
          <select
            v-model="boardFilters.teamId"
            class="h-9 w-full appearance-none rounded-lg border border-n-weak bg-n-solid-2 py-2 pl-3 pr-9 text-sm text-n-slate-12"
          >
            <option value="">{{ t('SALES_PIPELINE.ALL_TEAMS') }}</option>
            <option v-for="team in teams" :key="team.id" :value="team.id">
              {{ team.name }}
            </option>
          </select>
          <i
            class="pointer-events-none absolute bottom-2.5 right-3 i-lucide-chevron-down size-4 text-n-slate-10"
          />
        </label>
        <label
          class="relative flex h-14 w-52 shrink-0 flex-col gap-1 text-xs font-medium text-n-slate-11"
        >
          <span>{{ t('SALES_PIPELINE.ASSIGNEE') }}</span>
          <select
            v-model="boardFilters.assigneeId"
            class="h-9 w-full appearance-none rounded-lg border border-n-weak bg-n-solid-2 py-2 pl-3 pr-9 text-sm text-n-slate-12"
          >
            <option value="">{{ t('SALES_PIPELINE.ALL_ASSIGNEES') }}</option>
            <option v-for="agent in agents" :key="agent.id" :value="agent.id">
              {{ agent.name }}
            </option>
          </select>
          <i
            class="pointer-events-none absolute bottom-2.5 right-3 i-lucide-chevron-down size-4 text-n-slate-10"
          />
        </label>
        <div class="flex shrink-0 flex-wrap gap-1.5">
          <button
            type="button"
            class="h-9 rounded-lg border px-3 text-xs font-medium transition"
            :class="
              boardFilters.quickFilter === 'stale'
                ? 'border-n-amber-11 bg-n-amber-11/10 text-n-amber-11'
                : 'border-n-weak text-n-slate-11 hover:bg-n-solid-2'
            "
            @click="
              boardFilters.quickFilter =
                boardFilters.quickFilter === 'stale' ? '' : 'stale'
            "
          >
            {{ t('SALES_PIPELINE.STALE') }}
          </button>
          <button
            type="button"
            class="h-9 rounded-lg border px-3 text-xs font-medium transition"
            :class="
              boardFilters.quickFilter === 'overdue_actions'
                ? 'border-n-ruby-11 bg-n-ruby-11/10 text-n-ruby-11'
                : 'border-n-weak text-n-slate-11 hover:bg-n-solid-2'
            "
            @click="
              boardFilters.quickFilter =
                boardFilters.quickFilter === 'overdue_actions'
                  ? ''
                  : 'overdue_actions'
            "
          >
            {{ t('SALES_PIPELINE.OVERDUE') }}
          </button>
        </div>
        <button
          type="button"
          class="h-9 shrink-0 rounded-lg bg-n-brand px-4 text-sm font-medium text-white"
          @click="applyBoardFilters"
        >
          {{ t('SALES_PIPELINE.APPLY') }}
        </button>
        <button
          type="button"
          class="h-9 shrink-0 rounded-lg px-3 text-sm font-medium text-n-slate-11 hover:bg-n-solid-2"
          @click="clearBoardFilters"
        >
          {{ t('SALES_PIPELINE.CLEAR') }}
        </button>
      </div>
      <p class="mt-2 text-xs text-n-slate-10">
        {{ t('SALES_PIPELINE.ACTIVITY_HELP') }}
      </p>
    </section>

    <div
      v-if="loading"
      class="grid min-h-0 flex-1 place-content-center text-n-slate-11"
    >
      {{ t('SALES_PIPELINE.LOADING') }}
    </div>
    <section
      v-else
      class="min-h-0 min-w-0 flex-1 overflow-x-auto overflow-y-hidden p-5 pb-6"
    >
      <div class="flex h-full min-w-max gap-4 pr-5">
        <section
          v-for="stage in activeStages"
          :key="stage.id"
          class="min-h-0 w-80 shrink-0 flex flex-col rounded-xl bg-n-solid-1 border border-n-weak"
          @dragover.prevent
          @drop="move(stage)"
        >
          <header
            class="px-4 py-3 flex justify-between items-center border-b border-n-weak"
          >
            <div class="flex items-center gap-2">
              <span
                class="size-2.5 rounded-full"
                :style="{ backgroundColor: stage.color }"
              />
              <h2 class="font-semibold text-sm text-n-slate-12">
                {{ stage.name }}
              </h2>
            </div>
            <span
              class="text-xs rounded-full bg-n-slate-3 px-2 py-0.5 text-n-slate-11"
              >{{
                stageMeta[stage.id]?.total_count ??
                opportunitiesFor(stage.id).length
              }}</span
            >
          </header>
          <div class="min-h-0 flex-1 p-3 space-y-3 overflow-y-auto">
            <article
              v-for="opportunity in opportunitiesFor(stage.id)"
              :key="opportunity.id"
              :draggable="stage.kind !== 'lost'"
              class="rounded-lg bg-n-solid-2 border border-n-weak p-3"
              :class="
                stage.kind === 'lost'
                  ? 'cursor-not-allowed opacity-95'
                  : 'cursor-grab active:cursor-grabbing'
              "
              :title="
                stage.kind === 'lost' ? t('SALES_PIPELINE.LOST_FINAL') : ''
              "
              @dragstart="
                stage.kind !== 'lost' && (draggingId = opportunity.id)
              "
            >
              <div class="flex items-start justify-between gap-2">
                <p class="min-w-0 font-medium text-sm text-n-slate-12">
                  {{
                    contactDisplayName(opportunity.conversation?.contact) ||
                    opportunity.title
                  }}
                </p>
                <span
                  class="shrink-0 rounded-full px-2 py-0.5 text-[11px]"
                  :class="healthClass(opportunity.health_status)"
                  >{{ healthLabel(opportunity.health_status) }}</span
                >
              </div>
              <p
                v-if="
                  opportunity.conversation?.lumerai_summary ||
                  opportunity.custom_attributes?.lumerai_summary
                "
                class="mt-2 line-clamp-3 text-xs leading-5 text-n-slate-11"
              >
                {{
                  opportunity.conversation?.lumerai_summary ||
                  opportunity.custom_attributes?.lumerai_summary
                }}
              </p>
              <div
                class="flex justify-between gap-2 mt-3 text-xs text-n-slate-11"
              >
                <span>{{
                  currency(
                    opportunity.display_amount ?? opportunity.amount,
                    opportunity.display_currency || opportunity.currency
                  )
                }}</span
                ><span>{{
                  opportunity.assignee?.name ||
                  opportunity.team?.name ||
                  t('SALES_PIPELINE.UNASSIGNED')
                }}</span>
              </div>
              <p
                v-if="opportunity.conversation"
                class="mt-2 truncate text-xs text-n-slate-10"
                :title="opportunity.conversation.last_message?.content"
              >
                {{ t('SALES_PIPELINE.LAST_MESSAGE') }}
                {{ t('COMMON.BULLET_SEPARATOR') }}
                {{
                  formatDateTime(
                    opportunity.conversation.last_message?.created_at
                  )
                }}
              </p>
              <p
                v-if="stage.kind === 'lost' && opportunity.lost_reason"
                class="mt-2 line-clamp-2 text-xs text-n-ruby-11"
              >
                <i class="i-lucide-circle-x mr-1 inline-block size-3" />{{
                  t('SALES_PIPELINE.LOST_REASON')
                }}
                {{ opportunity.lost_reason }}
              </p>
              <p
                v-if="opportunity.next_action"
                class="mt-2 line-clamp-2 text-xs text-n-slate-11"
              >
                <i class="i-lucide-list-check mr-1 inline-block size-3" />{{
                  opportunity.next_action
                }}<span v-if="opportunity.next_action_at">
                  {{ t('COMMON.BULLET_SEPARATOR') }}
                  {{ formatDateTime(opportunity.next_action_at) }}</span
                >
              </p>
              <p v-else class="mt-2 text-xs text-n-amber-11">
                <i class="i-lucide-triangle-alert mr-1 inline-block size-3" />{{
                  t('SALES_PIPELINE.DEFINE_NEXT_ACTION')
                }}
              </p>
              <footer
                class="mt-3 flex items-center justify-between gap-2 border-t border-n-weak pt-2"
                @dragstart.stop
              >
                <button
                  v-if="opportunity.conversation"
                  type="button"
                  class="inline-flex h-8 items-center gap-1.5 rounded-md border border-n-weak px-2 text-xs font-medium text-n-slate-11 transition hover:border-n-brand hover:bg-n-brand/10 hover:text-n-brand disabled:cursor-not-allowed disabled:opacity-60"
                  :disabled="
                    opportunity.conversation.status === 'resolved' ||
                    resolvingConversationId === opportunity.conversation.id
                  "
                  :title="
                    opportunity.conversation.status === 'resolved'
                      ? t('SALES_PIPELINE.CONVERSATION_RESOLVED')
                      : t('SALES_PIPELINE.RESOLVE_CONVERSATION')
                  "
                  @click.stop="resolveConversation(opportunity)"
                >
                  <i class="i-lucide-circle-check size-4" />{{
                    opportunity.conversation.status === 'resolved'
                      ? t('SALES_PIPELINE.RESOLVED')
                      : t('SALES_PIPELINE.RESOLVE')
                  }}
                </button>
                <span v-else />
                <div class="flex items-center gap-1">
                  <button
                    v-if="opportunity.conversation"
                    type="button"
                    class="grid size-8 place-items-center rounded-md text-n-slate-11 transition hover:bg-n-brand/10 hover:text-n-brand"
                    :title="t('SALES_PIPELINE.OPEN_CONVERSATION')"
                    :aria-label="t('SALES_PIPELINE.OPEN_CONVERSATION')"
                    @click.stop="openConversation(opportunity)"
                  >
                    <i class="i-logos-whatsapp-icon size-4" />
                  </button>
                  <router-link
                    v-if="opportunity.conversation?.ticket"
                    :to="{
                      name: 'tickets_dashboard_index',
                      params: { accountId },
                      query: { ticket_id: opportunity.conversation.ticket.id },
                    }"
                    class="grid size-8 place-items-center rounded-md text-n-slate-11 transition hover:bg-n-brand/10 hover:text-n-brand"
                    :title="t('SALES_PIPELINE.OPEN_TICKET')"
                    :aria-label="t('SALES_PIPELINE.OPEN_TICKET')"
                    @click.stop
                  >
                    <i class="i-lucide-ticket size-4" />
                  </router-link>
                  <router-link
                    v-if="opportunity.conversation?.calendar_appointment"
                    :to="{
                      name: 'calendar_index',
                      params: { accountId },
                      query: {
                        appointment_id:
                          opportunity.conversation.calendar_appointment.id,
                      },
                    }"
                    class="grid size-8 place-items-center rounded-md text-n-slate-11 transition hover:bg-n-brand/10 hover:text-n-brand"
                    :title="t('SALES_PIPELINE.OPEN_CALENDAR')"
                    :aria-label="t('SALES_PIPELINE.OPEN_CALENDAR')"
                    @click.stop
                  >
                    <i class="i-lucide-calendar-days size-4" />
                  </router-link>
                  <button
                    v-if="opportunity.shopify_checkout"
                    type="button"
                    class="relative grid size-8 place-items-center rounded-md text-n-slate-11 transition hover:bg-n-brand/10 hover:text-n-brand"
                    :title="
                      t('SALES_PIPELINE.SHOPIFY_CART_WITH_ITEMS', {
                        count: opportunity.shopify_checkout.item_count,
                      })
                    "
                    :aria-label="t('SALES_PIPELINE.SHOPIFY_CART')"
                    @click.stop="openShopifyCheckout(opportunity)"
                  >
                    <i class="i-lucide-shopping-cart size-4" /><span
                      class="absolute -right-1 -top-1 grid min-w-4 h-4 place-items-center rounded-full bg-n-brand px-1 text-[9px] font-bold leading-none text-white"
                      >{{ opportunity.shopify_checkout.item_count }}</span
                    >
                  </button>
                  <button
                    type="button"
                    class="grid size-8 place-items-center rounded-md text-n-slate-11 transition hover:bg-n-brand/10 hover:text-n-brand"
                    :title="t('SALES_PIPELINE.AI_CONTEXT')"
                    :aria-label="t('SALES_PIPELINE.AI_CONTEXT')"
                    @click.stop="openAiContext(opportunity)"
                  >
                    <i class="i-lucide-brain-circuit size-4" />
                  </button>
                  <button
                    type="button"
                    class="inline-flex size-8 shrink-0 items-center justify-center rounded-md text-n-slate-11 transition hover:bg-n-brand/10 hover:text-n-brand focus:outline-none focus-visible:ring-2 focus-visible:ring-n-brand focus-visible:ring-offset-2 focus-visible:ring-offset-n-solid-2"
                    :title="t('SALES_PIPELINE.OPPORTUNITY_DETAILS')"
                    :aria-label="t('SALES_PIPELINE.OPPORTUNITY_DETAILS')"
                    @click.stop="openOpportunityDetails(opportunity)"
                  >
                    <i class="i-lucide-info size-4" />
                  </button>
                </div>
              </footer>
            </article>
            <p
              v-if="
                !loadingStages[stage.id] && !opportunitiesFor(stage.id).length
              "
              class="text-xs text-n-slate-10 text-center py-6"
            >
              {{ t('SALES_PIPELINE.NO_OPPORTUNITIES') }}
            </p>
            <button
              v-if="stageMeta[stage.id]?.next_page"
              type="button"
              class="w-full rounded-lg border border-n-weak px-3 py-2 text-xs font-medium text-n-slate-11 transition hover:bg-n-solid-2 disabled:cursor-not-allowed disabled:opacity-60"
              :disabled="loadingStages[stage.id]"
              @click="loadStage(stage.id)"
            >
              {{
                loadingStages[stage.id]
                  ? t('SALES_PIPELINE.LOADING_MORE')
                  : t('SALES_PIPELINE.LOAD_MORE', {
                      count:
                        stageMeta[stage.id].total_count -
                        opportunitiesFor(stage.id).length,
                    })
              }}
            </button>
            <p
              v-else-if="loadingStages[stage.id]"
              class="py-2 text-center text-xs text-n-slate-10"
            >
              {{ t('SALES_PIPELINE.LOADING_OPPORTUNITIES') }}
            </p>
          </div>
        </section>
      </div>
    </section>

    <div
      v-if="
        showShopifyCheckoutModal && selectedShopifyOpportunity?.shopify_checkout
      "
      class="fixed inset-0 z-[60] grid place-items-center bg-n-black/60 p-4"
      @click.self="showShopifyCheckoutModal = false"
    >
      <section
        class="w-full max-w-lg rounded-xl border border-n-weak bg-n-solid-1 p-5 shadow-xl"
      >
        <header class="flex items-start justify-between gap-4">
          <div>
            <h2 class="text-lg font-semibold text-n-slate-12">
              {{ t('SALES_PIPELINE.SHOPIFY_CART_TITLE') }}
            </h2>
            <p class="mt-1 text-sm text-n-slate-11">
              {{ contactDisplayName(selectedShopifyOpportunity.contact) }}
              {{ t('COMMON.BULLET_SEPARATOR') }}
              {{ selectedShopifyOpportunity.shopify_checkout.item_count }}
              {{ t('SALES_PIPELINE.ITEMS') }}
            </p>
          </div>
          <button
            type="button"
            class="text-sm font-medium text-n-slate-11 hover:text-n-slate-12"
            @click="showShopifyCheckoutModal = false"
          >
            {{ t('SALES_PIPELINE.CLOSE') }}
          </button>
        </header>
        <p class="mt-4 text-sm">
          {{ t('SALES_PIPELINE.SHOPIFY_STATUS') }}
          <strong
            :class="
              shopifyCheckoutClass(
                selectedShopifyOpportunity.shopify_checkout.status
              )
            "
            >{{
              shopifyCheckoutLabel(
                selectedShopifyOpportunity.shopify_checkout.status
              )
            }}</strong
          >
        </p>
        <div class="mt-4 max-h-80 space-y-3 overflow-y-auto pr-1">
          <div
            v-for="item in selectedShopifyOpportunity.shopify_checkout.items"
            :key="item.id"
            class="flex items-start justify-between gap-4 rounded-lg border border-n-weak bg-n-solid-2 p-3"
          >
            <div class="min-w-0">
              <p class="font-medium text-sm text-n-slate-12">
                {{ item.product_title }}
              </p>
              <p v-if="item.variant_title" class="mt-1 text-xs text-n-slate-11">
                {{ item.variant_title }}
              </p>
              <p class="mt-1 text-xs text-n-slate-10">
                {{ item.quantity }} {{ t('SALES_PIPELINE.MULTIPLY') }}
                {{ currency(item.unit_price, item.currency) }}
              </p>
            </div>
            <p class="shrink-0 text-sm font-medium text-n-slate-12">
              {{ currency(item.quantity * item.unit_price, item.currency) }}
            </p>
          </div>
          <p
            v-if="!selectedShopifyOpportunity.shopify_checkout.items?.length"
            class="py-5 text-center text-sm text-n-slate-10"
          >
            {{ t('SALES_PIPELINE.EMPTY_CART') }}
          </p>
        </div>
      </section>
    </div>

    <div
      v-if="showWonConfirmationModal"
      class="fixed inset-0 z-50 grid place-items-center bg-n-black/60 p-4"
      @click.self="cancelWonMove"
    >
      <section
        class="w-full max-w-lg rounded-xl border border-n-weak bg-n-solid-1 p-5 shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="won-confirmation-title"
      >
        <header class="flex items-start justify-between gap-4">
          <div>
            <h2
              id="won-confirmation-title"
              class="text-lg font-semibold text-n-slate-12"
            >
              {{ t('SALES_PIPELINE.CONFIRM_WON') }}
            </h2>
            <p class="mt-1 text-sm text-n-slate-11">
              {{ t('SALES_PIPELINE.WON_HELP') }}
            </p>
          </div>
          <button
            type="button"
            class="text-sm text-n-slate-11"
            @click="cancelWonMove"
          >
            {{ t('SALES_PIPELINE.CLOSE') }}
          </button>
        </header>
        <div
          class="mt-4 rounded-lg border border-n-teal-11/30 bg-n-teal-11/10 p-3 text-sm text-n-slate-12"
        >
          <p>
            <span class="text-n-slate-10">{{
              t('SALES_PIPELINE.CONTACT')
            }}</span>
            {{ contactDisplayName(pendingWonMove?.opportunity.contact) }}
          </p>
          <p class="mt-1">
            <span class="text-n-slate-10">{{ t('SALES_PIPELINE.VALUE') }}</span>
            {{
              currency(
                pendingWonMove?.opportunity.amount,
                pendingWonMove?.opportunity.currency
              )
            }}
          </p>
        </div>
        <footer class="mt-4 flex justify-end gap-3">
          <button
            type="button"
            class="rounded-lg border border-n-weak px-4 py-2 text-sm font-medium text-n-slate-12 transition hover:bg-n-solid-2"
            @click="cancelWonMove"
          >
            {{ t('SALES_PIPELINE.CANCEL') }}
          </button>
          <button
            type="button"
            class="rounded-lg bg-n-teal px-4 py-2 text-sm font-medium text-white"
            @click="confirmWonMove"
          >
            {{ t('SALES_PIPELINE.CONFIRM_WON_ACTION') }}
          </button>
        </footer>
      </section>
    </div>

    <div
      v-if="showLostReasonModal"
      class="fixed inset-0 z-50 grid place-items-center bg-n-black/60 p-4"
      @click.self="cancelLostMove"
    >
      <section
        class="w-full max-w-lg rounded-xl border border-n-weak bg-n-solid-1 p-5 shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="lost-reason-title"
      >
        <header class="flex items-start justify-between gap-4">
          <div>
            <h2
              id="lost-reason-title"
              class="text-lg font-semibold text-n-slate-12"
            >
              {{ t('SALES_PIPELINE.REGISTER_LOSS') }}
            </h2>
            <p class="mt-1 text-sm text-n-slate-11">
              {{ t('SALES_PIPELINE.LOSS_HELP') }}
            </p>
          </div>
          <button
            type="button"
            class="text-sm text-n-slate-11"
            @click="cancelLostMove"
          >
            {{ t('SALES_PIPELINE.CLOSE') }}
          </button>
        </header>
        <textarea
          v-model.trim="lostReasonDraft"
          autofocus
          rows="4"
          class="mt-4 w-full resize-y rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand"
          :placeholder="t('SALES_PIPELINE.LOSS_PLACEHOLDER')"
          @keydown.ctrl.enter="confirmLostMove"
        />
        <footer class="mt-4 flex justify-end gap-3">
          <button
            type="button"
            class="rounded-lg border border-n-weak px-4 py-2 text-sm font-medium text-n-slate-12 transition hover:bg-n-solid-2"
            @click="cancelLostMove"
          >
            {{ t('SALES_PIPELINE.CANCEL') }}
          </button>
          <button
            type="button"
            class="rounded-lg bg-n-brand px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-60"
            :disabled="!lostReasonDraft.trim()"
            @click="confirmLostMove"
          >
            {{ t('SALES_PIPELINE.CONFIRM_LOSS') }}
          </button>
        </footer>
      </section>
    </div>

    <div
      v-if="showConversationPreview && selectedOpportunity?.conversation"
      class="fixed inset-0 z-50 flex justify-end bg-n-black/60"
      @click.self="showConversationPreview = false"
    >
      <section
        class="flex h-full w-full max-w-2xl flex-col border-l border-n-weak bg-n-solid-1 shadow-xl"
      >
        <header
          class="flex items-start justify-between gap-4 border-b border-n-weak p-5"
        >
          <div class="min-w-0">
            <div class="flex items-center gap-2">
              <i class="i-logos-whatsapp-icon size-5 text-[#25D366]" />
              <h2 class="text-lg font-semibold text-n-slate-12">
                {{
                  t('SALES_PIPELINE.CONVERSATION', {
                    id: selectedOpportunity.conversation.display_id,
                  })
                }}
              </h2>
            </div>
            <p class="mt-1 truncate text-sm text-n-slate-11">
              {{ contactDisplayName(selectedOpportunity.conversation.contact) }}
            </p>
          </div>
          <button
            type="button"
            class="grid size-9 place-items-center rounded-md text-n-slate-11 transition hover:bg-n-solid-2 hover:text-n-slate-12"
            :aria-label="t('SALES_PIPELINE.CLOSE_PREVIEW')"
            :title="t('SALES_PIPELINE.CLOSE')"
            @click="showConversationPreview = false"
          >
            <i class="i-lucide-x size-5" />
          </button>
        </header>

        <div class="flex-1 space-y-3 overflow-y-auto p-5">
          <p
            v-if="loadingConversationPreview"
            class="py-12 text-center text-sm text-n-slate-10"
          >
            {{ t('SALES_PIPELINE.LOADING_MESSAGES') }}
          </p>
          <p
            v-else-if="!previewMessages.length"
            class="py-12 text-center text-sm text-n-slate-10"
          >
            {{ t('SALES_PIPELINE.NO_MESSAGES') }}
          </p>
          <template v-else>
            <article
              v-for="message in previewMessages"
              :key="message.id"
              class="max-w-[85%] rounded-xl px-4 py-3"
              :class="[
                isIncomingMessage(message)
                  ? 'mr-auto bg-n-solid-2 text-n-slate-12'
                  : 'ml-auto bg-n-brand/15 text-n-slate-12',
                message.private ? 'border border-n-amber/40 bg-n-amber/10' : '',
              ]"
            >
              <p class="text-xs font-medium text-n-slate-10">
                {{
                  message.private
                    ? t('SALES_PIPELINE.INTERNAL_NOTE')
                    : isIncomingMessage(message)
                      ? message.sender?.name || t('SALES_PIPELINE.CUSTOMER')
                      : message.sender?.name || t('SALES_PIPELINE.TEAM_LABEL')
                }}
              </p>
              <p class="mt-1 whitespace-pre-wrap break-words text-sm leading-6">
                {{
                  message.content ||
                  (message.attachments?.length
                    ? t('SALES_PIPELINE.MESSAGE_WITH_ATTACHMENT')
                    : t('SALES_PIPELINE.MESSAGE_WITHOUT_CONTENT'))
                }}
              </p>
              <p class="mt-2 text-right text-xs text-n-slate-10">
                {{
                  formatDateTime(
                    message.created_at ? message.created_at * 1000 : null
                  )
                }}
              </p>
            </article>
          </template>
        </div>

        <footer
          class="flex flex-wrap items-center justify-between gap-3 border-t border-n-weak p-5"
        >
          <button
            v-if="selectedOpportunity.conversation.status !== 'resolved'"
            type="button"
            class="inline-flex items-center gap-2 rounded-lg border border-n-weak px-3 py-2 text-sm font-medium text-n-slate-12 transition hover:bg-n-solid-2 disabled:cursor-not-allowed disabled:opacity-60"
            :disabled="
              resolvingConversationId === selectedOpportunity.conversation.id
            "
            @click="resolveConversation(selectedOpportunity)"
          >
            <i class="i-lucide-circle-check size-4" />{{
              t('SALES_PIPELINE.RESOLVE')
            }}
          </button>
          <span
            v-else
            class="inline-flex items-center gap-2 text-sm font-medium text-n-slate-10"
            ><i class="i-lucide-circle-check size-4" />{{
              t('SALES_PIPELINE.CONVERSATION_RESOLVED_LABEL')
            }}</span
          >
          <button
            type="button"
            class="inline-flex items-center gap-2 rounded-lg bg-n-brand px-3 py-2 text-sm font-medium text-white"
            @click="openFullConversation(selectedOpportunity)"
          >
            <i class="i-lucide-external-link size-4" />{{
              t('SALES_PIPELINE.OPEN_FULL_CONVERSATION')
            }}
          </button>
        </footer>
      </section>
    </div>

    <div
      v-if="showAiContext && selectedOpportunity"
      class="fixed inset-0 z-50 grid place-items-center bg-n-black/60 p-4"
      @click.self="showAiContext = false"
    >
      <section
        class="w-full max-w-2xl rounded-xl border border-n-weak bg-n-solid-1 p-5 shadow-xl"
      >
        <header class="flex items-start justify-between gap-4">
          <div>
            <h2 class="text-lg font-semibold text-n-slate-12">
              {{ t('SALES_PIPELINE.AI_CONTEXT_TITLE') }}
            </h2>
            <p class="mt-1 text-sm text-n-slate-11">
              {{ t('SALES_PIPELINE.AI_CONTEXT_HELP') }}
            </p>
          </div>
          <button
            type="button"
            class="text-sm text-n-slate-11"
            @click="showAiContext = false"
          >
            {{ t('SALES_PIPELINE.CLOSE') }}
          </button>
        </header>
        <button
          v-if="selectedOpportunity.conversation"
          type="button"
          class="mt-4 inline-flex items-center gap-2 rounded-lg bg-n-brand px-3 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-60"
          :disabled="
            generatingSummaryConversationId ===
              selectedOpportunity.conversation.id ||
            !canGenerateSummary(selectedOpportunity.conversation)
          "
          :title="
            canGenerateSummary(selectedOpportunity.conversation)
              ? t('SALES_PIPELINE.GENERATE_SUMMARY_HELP')
              : t('SALES_PIPELINE.SUMMARY_CURRENT_HELP')
          "
          @click="generateSummary"
        >
          <i class="i-lucide-brain-circuit size-4" />{{
            generatingSummaryConversationId ===
            selectedOpportunity.conversation.id
              ? t('SALES_PIPELINE.GENERATING_SUMMARY')
              : canGenerateSummary(selectedOpportunity.conversation)
                ? t('SALES_PIPELINE.GENERATE_SUMMARY')
                : t('SALES_PIPELINE.SUMMARY_UPDATED')
          }}
        </button>
        <p
          v-if="
            selectedOpportunity.conversation &&
            !canGenerateSummary(selectedOpportunity.conversation)
          "
          class="mt-2 text-xs text-n-slate-10"
        >
          {{ t('SALES_PIPELINE.SUMMARY_CURRENT') }}
        </p>
        <div class="mt-5 max-h-[55vh] space-y-3 overflow-y-auto">
          <article
            v-for="summary in selectedOpportunity.conversation
              ?.lumerai_history || []"
            :key="summary.created_at"
            class="rounded-lg border border-n-weak bg-n-solid-2 p-3"
          >
            <p class="whitespace-pre-wrap text-sm leading-6 text-n-slate-12">
              {{ summary.content }}
            </p>
            <p class="mt-2 text-xs text-n-slate-10">
              {{ new Date(summary.created_at).toLocaleString('pt-BR') }}
            </p>
          </article>
          <p
            v-if="
              !(selectedOpportunity.conversation?.lumerai_history || []).length
            "
            class="rounded-lg border border-dashed border-n-weak p-5 text-center text-sm text-n-slate-11"
          >
            {{ t('SALES_PIPELINE.NO_SUMMARY') }}
          </p>
        </div>
      </section>
    </div>

    <div
      v-if="showOpportunityDetails && selectedOpportunity"
      class="fixed inset-0 z-50 grid place-items-center bg-n-black/60 p-4"
      @click.self="showOpportunityDetails = false"
    >
      <section
        class="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-xl border border-n-weak bg-n-solid-1 shadow-xl"
      >
        <header
          class="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-n-weak bg-n-solid-1 px-6 py-5"
        >
          <div>
            <h2 class="text-lg font-semibold text-n-slate-12">
              {{ t('SALES_PIPELINE.AUTO_011') }}
            </h2>
            <p class="mt-1 text-sm text-n-slate-11">
              {{ t('SALES_PIPELINE.AUTO_012') }}
            </p>
          </div>
          <button
            type="button"
            class="grid size-9 place-items-center rounded-lg text-n-slate-11 transition hover:bg-n-solid-2 hover:text-n-slate-12"
            :aria-label="t('SALES_PIPELINE.AUTO_001')"
            :title="t('SALES_PIPELINE.AUTO_002')"
            @click="showOpportunityDetails = false"
          >
            <i class="i-lucide-x size-5" />
          </button>
        </header>
        <div class="space-y-6 px-6 py-5">
          <dl class="grid gap-x-10 gap-y-5 text-sm sm:grid-cols-2">
            <div>
              <dt class="text-n-slate-10">
                {{ t('SALES_PIPELINE.AUTO_013') }}
              </dt>
              <dd class="mt-1 font-medium text-n-slate-12">
                {{ contactDisplayName(selectedOpportunity.contact) }}
              </dd>
              <p
                v-if="
                  selectedOpportunity.contact?.phone_number ||
                  selectedOpportunity.contact?.email
                "
                class="mt-1 truncate text-xs text-n-slate-10"
              >
                {{
                  selectedOpportunity.contact?.phone_number ||
                  selectedOpportunity.contact?.email
                }}
              </p>
            </div>
            <div>
              <dt class="text-n-slate-10">
                {{ t('SALES_PIPELINE.AUTO_009') }}
              </dt>
              <dd class="mt-1 font-medium text-n-slate-12">
                {{
                  currency(
                    selectedOpportunity.amount,
                    selectedOpportunity.currency
                  )
                }}
              </dd>
            </div>
            <div>
              <dt class="text-n-slate-10">
                {{ t('SALES_PIPELINE.AUTO_014') }}
              </dt>
              <dd class="mt-1 font-medium text-n-slate-12">
                {{ selectedOpportunity.assignee?.name || 'Não atribuído' }}
              </dd>
            </div>
            <div>
              <dt class="text-n-slate-10">
                {{ t('SALES_PIPELINE.AUTO_015') }}
              </dt>
              <dd class="mt-1 font-medium text-n-slate-12">
                {{
                  selectedOpportunity.sales_pipeline_stage?.name ||
                  'Não definida'
                }}
              </dd>
            </div>
            <div>
              <dt class="text-n-slate-10">
                {{ t('SALES_PIPELINE.AUTO_016') }}
              </dt>
              <dd class="mt-1 font-medium text-n-slate-12">
                {{
                  selectedOpportunity.expected_close_at
                    ? new Date(
                        selectedOpportunity.expected_close_at
                      ).toLocaleDateString('pt-BR')
                    : t('SALES_PIPELINE.NOT_INFORMED')
                }}
              </dd>
            </div>
            <div>
              <dt class="text-n-slate-10">
                {{ t('SALES_PIPELINE.AUTO_017') }}
              </dt>
              <dd class="mt-1 font-medium text-n-slate-12">
                {{
                  selectedOpportunity.conversation
                    ? `#${selectedOpportunity.conversation.display_id}`
                    : t('SALES_PIPELINE.NOT_LINKED')
                }}
              </dd>
            </div>
          </dl>
          <section
            v-if="selectedOpportunity.shopify_checkout"
            class="rounded-xl border border-n-brand/25 bg-n-brand/5 p-4"
          >
            <div class="flex flex-wrap items-center justify-between gap-3">
              <div class="flex items-center gap-3">
                <span
                  class="grid size-9 place-items-center rounded-lg bg-n-brand/10 text-n-brand"
                  ><i class="i-lucide-shopping-cart size-4"
                /></span>
                <div>
                  <h3 class="text-sm font-semibold text-n-slate-12">
                    {{ t('SALES_PIPELINE.AUTO_018') }}
                  </h3>
                  <p class="mt-0.5 text-xs text-n-slate-11">
                    {{ selectedOpportunity.shopify_checkout.item_count }}
                    {{ t('SALES_PIPELINE.ITEMS') }}
                    {{ t('COMMON.BULLET_SEPARATOR') }}
                    <strong
                      :class="
                        shopifyCheckoutClass(
                          selectedOpportunity.shopify_checkout.status
                        )
                      "
                      >{{
                        shopifyCheckoutLabel(
                          selectedOpportunity.shopify_checkout.status
                        )
                      }}</strong
                    >
                  </p>
                </div>
              </div>
              <button
                type="button"
                class="rounded-lg border border-n-brand/40 px-3 py-2 text-sm font-medium text-n-brand transition hover:bg-n-brand/10"
                @click="openShopifyCheckout(selectedOpportunity)"
              >
                {{ t('SALES_PIPELINE.AUTO_019') }}
              </button>
            </div>
          </section>
          <div
            v-if="selectedOpportunity.lost_reason"
            class="rounded-xl border border-n-ruby-11/30 bg-n-ruby-11/10 p-4"
          >
            <p class="text-sm font-medium text-n-ruby-11">
              {{ t('SALES_PIPELINE.AUTO_020') }}
            </p>
            <p class="mt-1 text-sm text-n-slate-12">
              {{ selectedOpportunity.lost_reason }}
            </p>
          </div>
        </div>
        <form
          class="border-t border-n-weak px-6 py-5"
          @submit.prevent="saveCommercialDetails"
        >
          <div class="flex flex-wrap items-center justify-between gap-3">
            <h3 class="text-sm font-semibold text-n-slate-12">
              {{ t('SALES_PIPELINE.AUTO_021') }}
            </h3>
            <button
              v-if="
                selectedOpportunity.conversation && !selectedOpportunityIsLost
              "
              type="button"
              class="inline-flex items-center gap-2 rounded-lg border border-n-brand px-3 py-2 text-sm font-medium text-n-brand transition hover:bg-n-brand/10 disabled:cursor-not-allowed disabled:opacity-60"
              :disabled="
                autofillingOpportunity ||
                !canAutofillWithAi(selectedOpportunity)
              "
              :title="
                canAutofillWithAi(selectedOpportunity)
                  ? 'Preencher os dados com base nas mensagens recentes'
                  : 'A LumerAI já analisou todas as mensagens deste atendimento'
              "
              @click="fillCommercialDetailsWithAi"
            >
              <i class="i-lucide-sparkles size-4" />{{
                autofillingOpportunity
                  ? t('SALES_PIPELINE.ANALYZING_CONVERSATION')
                  : canAutofillWithAi(selectedOpportunity)
                    ? 'Preencher com a LumerAI'
                    : 'Dados já analisados'
              }}
            </button>
          </div>
          <p
            v-if="selectedOpportunity.conversation"
            class="mt-2 text-xs text-n-slate-10"
          >
            {{ t('SALES_PIPELINE.AUTO_022')
            }}<span v-if="!canAutofillWithAi(selectedOpportunity)">{{
              t('SALES_PIPELINE.AUTO_023')
            }}</span>
          </p>
          <fieldset
            class="mt-4 grid gap-x-4 gap-y-5 sm:grid-cols-2"
            :disabled="selectedOpportunityIsLost"
          >
            <label class="text-xs font-medium text-n-slate-11"
              >{{ t('SALES_PIPELINE.AUTO_024')
              }}<input
                v-model.trim="opportunityDraft.next_action"
                class="mt-1 h-11 w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 text-sm text-n-slate-12"
                :placeholder="t('SALES_PIPELINE.AUTO_003')"
            /></label>
            <label class="text-xs font-medium text-n-slate-11"
              >{{ t('SALES_PIPELINE.AUTO_025')
              }}<ScheduledDateTimePicker
                v-model="opportunityDraft.next_action_at"
                class="commercial-scheduled-picker mt-1"
            /></label>
            <div class="min-w-0 text-xs font-medium text-n-slate-11">
              <span>{{ t('SALES_PIPELINE.AUTO_009') }}</span>
              <div
                class="mt-1 grid h-11 grid-cols-[8.5rem_minmax(0,1fr)] rounded-lg border border-n-weak bg-n-solid-2 focus-within:border-n-brand"
              >
                <div class="relative h-11 min-w-0 border-r border-n-weak">
                  <button
                    type="button"
                    class="grid h-11 w-full grid-cols-[minmax(0,1fr)_2.5rem] items-center rounded-l-lg !m-0 !p-0 text-left text-xs font-semibold uppercase text-n-brand transition hover:bg-n-solid-3/50 focus:outline-none"
                    :aria-label="t('SALES_PIPELINE.AUTO_004')"
                    aria-haspopup="listbox"
                    :aria-expanded="showCurrencyMenu"
                    @click="showCurrencyMenu = !showCurrencyMenu"
                  >
                    <span
                      class="flex h-full min-w-0 items-center px-3 leading-none"
                      ><span class="truncate">{{
                        currencyLabel(opportunityDraft.currency)
                      }}</span></span
                    >
                    <span class="grid h-full place-items-center"
                      ><i class="size-4 i-lucide-chevron-down text-n-slate-10"
                    /></span>
                  </button>
                  <div
                    v-if="showCurrencyMenu"
                    class="absolute left-0 top-11 z-20 mt-1 w-full overflow-hidden rounded-lg border border-n-weak bg-n-solid-1 py-1 shadow-lg"
                    role="listbox"
                    :aria-label="t('SALES_PIPELINE.AUTO_005')"
                  >
                    <button
                      v-for="code in ['BRL', 'USD', 'EUR']"
                      :key="code"
                      type="button"
                      class="flex w-full items-center px-3 py-2 text-left text-xs font-semibold text-n-slate-12 hover:bg-n-solid-2"
                      role="option"
                      :aria-selected="opportunityDraft.currency === code"
                      @click="selectOpportunityCurrency(code)"
                    >
                      {{ currencyLabel(code) }}
                    </button>
                  </div>
                </div>
                <input
                  v-model="opportunityDraft.amount"
                  min="0"
                  step="0.01"
                  type="text"
                  inputmode="decimal"
                  class="number-input min-w-0 border-0 bg-transparent px-3 text-sm text-n-slate-12 outline-none"
                  :placeholder="t('SALES_PIPELINE.AUTO_006')"
                />
              </div>
            </div>
            <label class="text-xs font-medium text-n-slate-11"
              >{{ t('SALES_PIPELINE.AUTO_016')
              }}<ScheduledDateTimePicker
                v-model="opportunityDraft.expected_close_at"
                :show-time="false"
                class="commercial-scheduled-picker mt-1"
            /></label>
            <label class="text-xs font-medium text-n-slate-11"
              >{{ t('SALES_PIPELINE.AUTO_026') }}
              <div class="relative mt-1">
                <select
                  v-model="opportunityDraft.priority"
                  class="h-11 w-full appearance-none rounded-lg border border-n-weak bg-n-solid-2 pl-3 pr-10 text-sm text-n-slate-12"
                >
                  <option value="low">
                    {{ t('SALES_PIPELINE.AUTO_027') }}
                  </option>
                  <option value="normal">
                    {{ t('SALES_PIPELINE.AUTO_028') }}
                  </option>
                  <option value="high">
                    {{ t('SALES_PIPELINE.AUTO_029') }}
                  </option>
                  <option value="urgent">
                    {{ t('SALES_PIPELINE.AUTO_030') }}
                  </option></select
                ><i
                  class="pointer-events-none absolute inset-y-0 right-3 my-auto i-lucide-chevron-down size-4 text-n-slate-10"
                /></div
            ></label>
            <label class="text-xs font-medium text-n-slate-11"
              >{{ t('SALES_PIPELINE.AUTO_031')
              }}<input
                v-model.number="opportunityDraft.probability"
                inputmode="numeric"
                class="mt-1 h-11 w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 text-sm text-n-slate-12"
                :placeholder="t('SALES_PIPELINE.AUTO_007')"
            /></label>
            <label class="text-xs font-medium text-n-slate-11"
              >{{ t('SALES_PIPELINE.AUTO_032') }}
              <div class="relative mt-1">
                <select
                  v-model="opportunityDraft.forecast_category"
                  class="h-11 w-full appearance-none rounded-lg border border-n-weak bg-n-solid-2 pl-3 pr-10 text-sm text-n-slate-12"
                >
                  <option value="pipeline">
                    {{ t('SALES_PIPELINE.AUTO_033') }}
                  </option>
                  <option value="best_case">
                    {{ t('SALES_PIPELINE.AUTO_034') }}
                  </option>
                  <option value="committed">
                    {{ t('SALES_PIPELINE.AUTO_035') }}
                  </option>
                  <option value="omitted">
                    {{ t('SALES_PIPELINE.AUTO_036') }}
                  </option></select
                ><i
                  class="pointer-events-none absolute inset-y-0 right-3 my-auto i-lucide-chevron-down size-4 text-n-slate-10"
                /></div
            ></label>
          </fieldset>
          <p
            v-if="selectedOpportunityIsLost"
            class="mt-3 text-xs text-n-ruby-11"
          >
            {{ t('SALES_PIPELINE.AUTO_037') }}
          </p>
          <div v-else class="mt-4 flex justify-end">
            <button
              class="rounded-lg bg-n-brand px-4 py-2 text-sm font-medium text-white"
            >
              {{ t('SALES_PIPELINE.AUTO_038') }}
            </button>
          </div>
        </form>
        <section class="border-t border-n-weak px-6 py-5">
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ t('SALES_PIPELINE.AUTO_039') }}
          </h3>
          <p
            v-if="loadingOpportunityDetails"
            class="mt-3 text-sm text-n-slate-11"
          >
            {{ t('SALES_PIPELINE.AUTO_040') }}
          </p>
          <template v-else-if="selectedOpportunity.events?.length">
            <ol class="mt-3 space-y-3 border-l border-n-weak pl-4">
              <li
                v-for="event in selectedOpportunity.events"
                :key="event.id"
                class="relative text-sm"
              >
                <span
                  class="absolute -left-[1.29rem] top-1.5 size-2 rounded-full bg-n-brand"
                />
                <p class="font-medium text-n-slate-12">
                  {{ eventLabel(event) }}
                </p>
                <p
                  v-if="stageTransition(event)"
                  class="mt-0.5 text-xs font-medium text-n-slate-11"
                >
                  {{ stageTransition(event) }}
                </p>
                <p class="mt-0.5 text-xs text-n-slate-10">
                  {{ new Date(event.created_at).toLocaleString('pt-BR')
                  }}<span v-if="event.actor?.name">
                    {{ t('COMMON.BULLET_SEPARATOR') }}
                    {{ event.actor.name }}</span
                  ><span v-if="event.source === 'lumerai'">{{
                    t('SALES_PIPELINE.AUTO_041')
                  }}</span>
                </p>
              </li>
            </ol>
            <button
              v-if="eventsMeta.next_page"
              type="button"
              class="mt-4 rounded-lg border border-n-weak px-3 py-2 text-sm font-medium text-n-slate-12 transition hover:bg-n-solid-2 disabled:cursor-not-allowed disabled:opacity-60"
              :disabled="loadingMoreEvents"
              @click="loadMoreEvents"
            >
              {{
                loadingMoreEvents
                  ? t('SALES_PIPELINE.LOADING')
                  : `Carregar mais (${Math.max(0, (eventsMeta.total_count || 0) - selectedOpportunity.events.length)} restantes)`
              }}
            </button>
          </template>
          <p v-else class="mt-3 text-sm text-n-slate-11">
            {{ t('SALES_PIPELINE.AUTO_042') }}
          </p>
        </section>
        <footer class="border-t border-n-weak px-6 py-5">
          <p class="text-xs text-n-slate-10">
            {{ t('SALES_PIPELINE.AUTO_043') }}
          </p>
        </footer>
      </section>
    </div>

    <div
      v-if="showCreate"
      class="fixed inset-0 z-50 grid place-items-center bg-n-black/60 p-4"
      @click.self="showCreate = false"
    >
      <form
        class="w-full max-w-xl rounded-xl bg-n-solid-1 border border-n-weak p-5 space-y-4"
        @submit.prevent="createOpportunity"
      >
        <div>
          <h2 class="text-lg font-semibold text-n-slate-12">
            {{ t('SALES_PIPELINE.AUTO_044') }}
          </h2>
          <p class="text-sm text-n-slate-11">
            {{ t('SALES_PIPELINE.AUTO_045') }}
          </p>
        </div>
        <label class="block text-sm font-medium text-n-slate-12"
          >{{ t('SALES_PIPELINE.AUTO_046')
          }}<input
            v-model="conversationQuery"
            class="mt-1 w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2"
            :placeholder="t('SALES_PIPELINE.AUTO_008')"
          />
        </label>
        <p v-if="searchingConversations" class="text-sm text-n-slate-11">
          {{ t('SALES_PIPELINE.AUTO_047') }}
        </p>
        <div
          v-else-if="conversationCandidates.length"
          class="max-h-56 space-y-2 overflow-y-auto rounded-lg border border-n-weak p-2"
        >
          <button
            v-for="conversation in conversationCandidates"
            :key="conversation.id"
            type="button"
            class="w-full rounded-md border p-3 text-left"
            :class="
              form.conversation?.id === conversation.id
                ? 'border-n-brand bg-n-brand/10'
                : 'border-n-weak bg-n-solid-2'
            "
            @click="form.conversation = conversation"
          >
            <span class="block text-sm font-medium text-n-slate-12"
              >{{ t('COMMON.HASH_PREFIX') }}{{ conversation.display_id }}
              {{ t('COMMON.BULLET_SEPARATOR') }}
              {{ contactDisplayName(conversation.contact) }}</span
            >
            <span class="mt-1 block truncate text-xs text-n-slate-11"
              >{{
                conversation.contact.phone_number ||
                conversation.contact.email ||
                t('SALES_PIPELINE.NO_PHONE_OR_EMAIL')
              }}
              {{ t('COMMON.BULLET_SEPARATOR') }}
              {{
                conversation.assignee?.name ||
                conversation.team?.name ||
                t('SALES_PIPELINE.NO_ASSIGNEE')
              }}</span
            >
            <span
              v-if="conversation.lumerai_summary"
              class="mt-2 line-clamp-2 block text-xs text-n-slate-11"
              >{{ conversation.lumerai_summary }}</span
            >
          </button>
        </div>
        <p
          v-else-if="conversationQuery.trim().length >= 2"
          class="text-sm text-n-slate-11"
        >
          {{ t('SALES_PIPELINE.AUTO_048') }}
        </p>
        <div
          v-if="form.conversation"
          class="rounded-lg border border-n-brand/40 bg-n-brand/10 p-3 text-sm text-n-slate-12"
        >
          {{
            t('SALES_PIPELINE.SELECTED_CONVERSATION', {
              id: form.conversation.display_id,
            })
          }}
          {{ t('COMMON.BULLET_SEPARATOR') }}
          {{ contactDisplayName(form.conversation.contact) }}
        </div>
        <details class="rounded-lg border border-n-weak p-3">
          <summary class="cursor-pointer text-sm font-medium text-n-slate-12">
            {{ t('SALES_PIPELINE.AUTO_049') }}
          </summary>
          <div class="mt-4 grid gap-x-4 gap-y-5 sm:grid-cols-2">
            <div class="min-w-0 text-xs font-medium text-n-slate-11">
              <span>{{ t('SALES_PIPELINE.AUTO_009') }}</span>
              <div
                class="mt-1 grid h-11 grid-cols-[8.5rem_minmax(0,1fr)] rounded-lg border border-n-weak bg-n-solid-2 focus-within:border-n-brand"
              >
                <div class="relative h-11 min-w-0 border-r border-n-weak">
                  <button
                    type="button"
                    class="grid h-11 w-full grid-cols-[minmax(0,1fr)_2.5rem] items-center rounded-l-lg !m-0 !p-0 text-left text-xs font-semibold uppercase text-n-brand transition hover:bg-n-solid-3/50 focus:outline-none"
                    :aria-label="t('SALES_PIPELINE.AUTO_004')"
                    aria-haspopup="listbox"
                    :aria-expanded="showCreateCurrencyMenu"
                    @click="showCreateCurrencyMenu = !showCreateCurrencyMenu"
                  >
                    <span
                      class="flex h-full min-w-0 items-center px-3 leading-none"
                      ><span class="truncate">{{
                        currencyLabel(form.currency)
                      }}</span></span
                    >
                    <span class="grid h-full place-items-center"
                      ><i class="size-4 i-lucide-chevron-down text-n-slate-10"
                    /></span>
                  </button>
                  <div
                    v-if="showCreateCurrencyMenu"
                    class="absolute left-0 top-11 z-20 mt-1 w-full overflow-hidden rounded-lg border border-n-weak bg-n-solid-1 py-1 shadow-lg"
                    role="listbox"
                    :aria-label="t('SALES_PIPELINE.AUTO_005')"
                  >
                    <button
                      v-for="code in ['BRL', 'USD', 'EUR']"
                      :key="code"
                      type="button"
                      class="flex w-full items-center px-3 py-2 text-left text-xs font-semibold text-n-slate-12 hover:bg-n-solid-2"
                      role="option"
                      :aria-selected="form.currency === code"
                      @click="selectCreateCurrency(code)"
                    >
                      {{ currencyLabel(code) }}
                    </button>
                  </div>
                </div>
                <input
                  v-model="form.amount"
                  min="0"
                  step="0.01"
                  type="text"
                  inputmode="decimal"
                  class="number-input min-w-0 border-0 bg-transparent px-3 text-sm text-n-slate-12 outline-none"
                  :placeholder="t('SALES_PIPELINE.AUTO_006')"
                />
              </div>
            </div>
            <label class="text-xs font-medium text-n-slate-11"
              >{{ t('SALES_PIPELINE.AUTO_016')
              }}<ScheduledDateTimePicker
                v-model="form.expected_close_at"
                :show-time="false"
                class="commercial-scheduled-picker mt-1"
            /></label>
          </div>
        </details>
        <div class="flex justify-end gap-3">
          <button
            type="button"
            class="px-4 py-2 text-sm"
            @click="showCreate = false"
          >
            {{ t('SALES_PIPELINE.AUTO_053') }}</button
          ><button
            class="px-4 py-2 rounded-lg bg-n-brand text-white text-sm font-medium"
          >
            {{ t('SALES_PIPELINE.AUTO_054') }}
          </button>
        </div>
      </form>
    </div>

    <div
      v-if="showSettings"
      class="fixed inset-0 z-50 grid place-items-center bg-n-black/60 p-4"
      @click.self="showSettings = false"
    >
      <form
        class="w-full max-w-3xl max-h-[85vh] overflow-y-auto rounded-xl bg-n-solid-1 border border-n-weak p-5 space-y-4"
        @submit.prevent="savePipelineSettings"
      >
        <div class="flex items-start justify-between gap-4">
          <div>
            <h2 class="text-lg font-semibold text-n-slate-12">
              {{ t('SALES_PIPELINE.AUTO_055') }}
            </h2>
            <p class="text-sm text-n-slate-11">
              {{ t('SALES_PIPELINE.AUTO_056') }}
            </p>
          </div>
          <button
            type="button"
            class="text-n-slate-11"
            @click="showSettings = false"
          >
            {{ t('SALES_PIPELINE.AUTO_002') }}
          </button>
        </div>
        <label class="block text-sm font-medium text-n-slate-12"
          >{{ t('SALES_PIPELINE.AUTO_057')
          }}<input
            v-model.trim="pipelineDraft.name"
            required
            class="mt-1 w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2"
        /></label>
        <div class="space-y-3">
          <div
            v-for="(stage, index) in pipelineDraft.stages"
            :key="stage.id || `new-${index}`"
            class="grid grid-cols-[minmax(0,1fr)_10rem_4.5rem_minmax(9rem,auto)] gap-3 items-start rounded-lg border border-n-weak p-3"
          >
            <label class="block text-xs text-n-slate-11"
              >{{ t('SALES_PIPELINE.AUTO_058')
              }}<input
                v-model.trim="stage.name"
                required
                class="mt-1 h-10 w-full rounded-md border border-n-weak bg-n-solid-2 px-2 text-sm text-n-slate-12"
            /></label>
            <label class="block text-xs text-n-slate-11"
              >{{ t('SALES_PIPELINE.AUTO_059')
              }}<span class="relative mt-1 block">
                <select
                  v-model="stage.kind"
                  class="h-10 w-full appearance-none rounded-md border border-n-weak bg-n-solid-2 py-0 pl-2 pr-8 text-sm text-n-slate-12"
                >
                  <option value="open">
                    {{ t('SALES_PIPELINE.AUTO_060') }}
                  </option>
                  <option value="review">
                    {{ t('SALES_PIPELINE.AUTO_061') }}
                  </option>
                  <option value="won">
                    {{ t('SALES_PIPELINE.AUTO_062') }}
                  </option>
                  <option value="lost">
                    {{ t('SALES_PIPELINE.AUTO_063') }}
                  </option>
                </select>
                <i
                  class="i-lucide-chevron-down pointer-events-none absolute inset-y-0 right-2 my-auto size-4 text-n-slate-11"
                />
              </span>
            </label>
            <label class="block text-xs text-n-slate-11"
              >{{ t('SALES_PIPELINE.AUTO_064')
              }}<input
                v-model="stage.color"
                type="color"
                class="mt-1 block h-10 w-12 rounded border border-n-weak bg-n-solid-2 p-1"
            /></label>
            <div class="text-xs text-n-slate-11">
              <span>{{ t('SALES_PIPELINE.AUTO_065') }}</span>
              <div class="mt-1 flex h-10 items-center gap-3">
                <label class="flex items-center gap-2 whitespace-nowrap"
                  ><input
                    v-model="stage.active"
                    type="checkbox"
                    class="size-4 rounded border-n-weak"
                  />{{ t('SALES_PIPELINE.AUTO_066') }}</label
                >
                <button
                  v-if="!stage.system_stage"
                  type="button"
                  class="text-sm text-n-ruby-10"
                  @click="removeDraftStage(stage, index)"
                >
                  {{ t('SALES_PIPELINE.AUTO_067') }}
                </button>
                <span v-else class="whitespace-nowrap text-n-slate-10">{{
                  t('SALES_PIPELINE.AUTO_068')
                }}</span>
              </div>
            </div>
            <div
              class="col-span-4 grid gap-3 border-t border-n-weak pt-3 sm:grid-cols-[minmax(0,1fr)_9rem_auto]"
            >
              <label class="block text-xs text-n-slate-11"
                >{{ t('SALES_PIPELINE.AUTO_069')
                }}<textarea
                  v-model.trim="stage.lumerai_instruction"
                  rows="2"
                  class="mt-1 w-full resize-y rounded-md border border-n-weak bg-n-solid-2 px-2 py-2 text-sm leading-5 text-n-slate-12"
                  :placeholder="t('SALES_PIPELINE.AUTO_010')"
                />
              </label>
              <label class="block text-xs text-n-slate-11"
                >{{ t('SALES_PIPELINE.AUTO_070')
                }}<input
                  v-model.number="stage.default_probability"
                  min="0"
                  max="100"
                  step="5"
                  type="number"
                  inputmode="numeric"
                  class="mt-1 h-10 w-full rounded-md border border-n-weak bg-n-solid-2 px-2 text-sm text-n-slate-12"
              /></label>
              <label
                class="flex items-center gap-2 self-end pb-2 text-xs text-n-slate-11"
                ><input
                  v-model="stage.ai_auto_move"
                  :disabled="stage.kind === 'won' || stage.kind === 'lost'"
                  type="checkbox"
                  class="size-4 rounded border-n-weak"
                />{{ t('SALES_PIPELINE.AUTO_071') }}</label
              >
            </div>
          </div>
        </div>
        <button
          type="button"
          class="px-3 py-2 rounded-lg border border-n-weak text-sm font-medium"
          @click="addStage"
        >
          {{ t('SALES_PIPELINE.AUTO_072') }}
        </button>
        <div class="flex justify-end gap-3">
          <button
            type="button"
            class="px-4 py-2 text-sm"
            @click="showSettings = false"
          >
            {{ t('SALES_PIPELINE.AUTO_053') }}</button
          ><button
            class="px-4 py-2 rounded-lg bg-n-brand text-white text-sm font-medium"
          >
            {{ t('SALES_PIPELINE.AUTO_073') }}
          </button>
        </div>
      </form>
    </div>

    <woot-confirm-modal
      ref="confirmDialog"
      :title="confirmTitle"
      :description="confirmDescription"
    />
  </main>
</template>

<style scoped>
.activity-date-picker :deep(button:first-child) {
  height: 2.25rem;
}

.commercial-scheduled-picker {
  height: 2.75rem;
}

.commercial-scheduled-picker :deep(button),
.commercial-scheduled-picker :deep(input) {
  height: 2.75rem;
}

.commercial-scheduled-picker :deep(button) {
  flex: 1;
  width: auto;
}

.commercial-scheduled-picker :deep(input) {
  width: 5.75rem;
}
</style>
